import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { BankAccountCard } from "@/components/funding/bank-account-card";
import { APPROVED_STATUS, BankAccountBadges } from "@/components/funding/bank-accounts";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { toast } from "@/lib/toast";
import type { AxiosError } from "axios";
import axios, { getErrorMessage, type ApiErrorResponse } from "@/lib/axios";
import { Trash01 } from "@untitledui/icons";
import type { BankAccountResponse } from "@fp/admin-api-client";
import { putApiAdminFundingAccountsAccountIdPaymentDestination } from "@fp/admin-api-client/funding/funding.client";

const REMOVE_ACCOUNT_ERROR_MESSAGES: Record<string, string> = {
  FUNDING_SOURCE: "Set another account as the funding source before removing this one.",
  ACCOUNT_IN_USE: "This account has a pending transaction and cannot be removed. Please wait for the transaction to complete before trying again.",
};

export const Route = createFileRoute("/funding/accounts/$accountId")({
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
  const { accountId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [nickname, setNickname] = useState("");

  const { data: accounts, isLoading } = useQuery({
    queryKey: ["fundingAccounts"],
    queryFn: async () => {
      const response = await axios.get<BankAccountResponse[]>("/api/admin/funding/accounts");
      return response.data;
    },
  });

  const account = accounts?.find((a) => a.id === accountId);

  const setFundingSourceMutation = useMutation({
    mutationFn: async () => {
      await axios.put(`/api/admin/funding/accounts/${accountId}/funding-source`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["fundingAccounts"] });
      toast.success("Funding source updated");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to set the funding source"));
    },
  });

  const setDepositDestinationMutation = useMutation({
    mutationFn: () => putApiAdminFundingAccountsAccountIdPaymentDestination(accountId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["fundingAccounts"] });
      toast.success("Deposit destination updated");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to set the deposit destination"));
    },
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      await axios.delete(`/api/admin/funding/accounts/${accountId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fundingAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["fundingStatus"] });
      toast.dismiss();
      toast.success("Account removed successfully");
      navigate({ to: "/funding" });
    },
    onError: (err: AxiosError<ApiErrorResponse>) => {
      toast.error(REMOVE_ACCOUNT_ERROR_MESSAGES[err.response?.data?.code ?? ""] ?? getErrorMessage(err, "Failed to remove account"));
    },
  });

  useEffect(() => {
    if (account) setNickname(account.nickname ?? "");
  }, [account]);


  const nicknameMutation = useMutation({
    mutationFn: async (value: string | null) => {
      await axios.patch(`/api/admin/funding/accounts/${accountId}/nickname`, { nickname: value || null });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fundingAccounts"] });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, "Failed to update nickname"));
    },
  });

  const handleNicknameBlur = () => {
    const trimmed = nickname.trim();
    const current = account?.nickname ?? "";
    if (trimmed !== current) {
      nicknameMutation.mutate(trimmed || null);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout isWide>
        <AdminPageHeader
          title="Bank Account"
          parent={{ url: "/funding", label: "Funding" }}
        />
        <PageContentContainer>
          <PageLoading />
        </PageContentContainer>
      </AdminLayout>
    );
  }

  if (!account) {
    return (
      <AdminLayout isWide>
        <AdminPageHeader
          title="Bank Account"
          parent={{ url: "/funding", label: "Funding" }}
        />
        <PageContentContainer>
          <div className="text-center text-gray-500 py-8">Account not found</div>
        </PageContentContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout isWide>
      <AdminPageHeader
        title="Bank Account"
        parent={{ url: "/funding", label: "Funding" }}
      />
      <PageContentContainer>
        <div className="space-y-6">
          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between gap-3">
              <BankAccountCard account={{ ...account, nickname: null }} />
              <BankAccountBadges account={account} />
            </div>
            {account.status === APPROVED_STATUS && (!account.isFundingSource || !account.isPaymentDestination) && (
              <div className="mt-4 space-y-3">
                {!account.isFundingSource && (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-gray-600">Your funding source pays your team and tops up your FieldPay Account.</p>
                    <Button color="secondary" size="sm" className="shrink-0" onClick={() => setFundingSourceMutation.mutate()} isLoading={setFundingSourceMutation.isPending}>
                      Use as funding source
                    </Button>
                  </div>
                )}
                {!account.isPaymentDestination && (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-gray-600">Your deposit destination receives payouts from organizations that pay you.</p>
                    <Button color="secondary" size="sm" className="shrink-0" onClick={() => setDepositDestinationMutation.mutate()} isLoading={setDepositDestinationMutation.isPending}>
                      Use as deposit destination
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nickname</label>
            <input type="text" value={nickname} onChange={(e) => setNickname(e.target.value)} onBlur={handleNicknameBlur} placeholder={account.institutionName} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" maxLength={100} disabled={nicknameMutation.isPending} />
            <p className="text-xs text-gray-500 mt-1">A friendly name for this account (optional)</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-red-600 mb-3">Danger Zone</h3>
            <div className="border border-red-200 rounded-lg">
              <div className="p-4 flex items-center justify-between gap-3">
                <div>
                  <h4 className="font-medium text-gray-900">Remove this account</h4>
                  <p className="text-sm text-gray-600">{account.isFundingSource && (accounts?.length ?? 0) > 1 ? "Set another account as the funding source before removing this one." : "This will unlink the account from your organization."}</p>
                </div>
                <Button color="secondary-destructive" size="sm" onClick={() => setShowRemoveConfirm(true)} iconLeading={Trash01} isDisabled={account.isFundingSource && (accounts?.length ?? 0) > 1}>
                  Remove
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PageContentContainer>

      <ConfirmationModal
        isOpen={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={() => removeMutation.mutate()}
        title="Remove Account"
        message={<><p className="mb-4">Are you sure you want to remove this account?</p><BankAccountCard account={account} className="p-3 bg-gray-50 rounded-lg" /></>}
        confirmText="Remove"
        confirmColor="primary-destructive"
        isLoading={removeMutation.isPending}
      />
    </AdminLayout>
  );
}